import {useContext, useEffect, useState} from 'react';

import dayjs, {Dayjs} from 'dayjs';
import {ErrorMessage} from 'formik';
import {useTranslation} from 'react-i18next';

import 'dayjs/locale/de';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {Box, IconButton, SxProps, Typography} from '@mui/material';
import {PickersDay} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {DateCalendar, PickersCalendarHeaderProps} from '@mui/x-date-pickers/DateCalendar';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';

import {getDateRangeSelectionAction} from './calendarDateRangeSelection';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';
interface CalendarProps {
  formik: any;
  isReadOnly?: boolean;
  syncDatesFromFormik?: boolean;
}

const parseFormikDate = (value?: string) => (value ? dayjs(value) : null);

export const Calendar = ({formik, isReadOnly, syncDatesFromFormik = false}: CalendarProps) => {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation();

  const shouldSyncFromFormik = Boolean(isReadOnly || syncDatesFromFormik);

  const getInitialStartDate = () => {
    if (!shouldSyncFromFormik) return null;
    return parseFormikDate(formik.values.startsAt);
  };

  const getInitialEndDate = () => {
    if (!shouldSyncFromFormik) return null;
    return parseFormikDate(formik.values.endsAt);
  };

  const [startDate, setStartDate] = useState<Dayjs | null>(getInitialStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(getInitialEndDate);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!shouldSyncFromFormik) return;

    setStartDate(parseFormikDate(formik.values.startsAt));
    setEndDate(parseFormikDate(formik.values.endsAt));
  }, [formik.values.startsAt, formik.values.endsAt, shouldSyncFromFormik]);

  const setStartDateValue = (date: Dayjs | null) => {
    setStartDate(date);
    if (date) {
      formik.setFieldValue('startsAt', date.format('YYYY-MM-DDTHH:mm:ss[Z]'));
    } else {
      formik.setFieldValue('startsAt', '');
    }
  };

  const setEndDateValue = (date: Dayjs | null) => {
    setEndDate(date);
    if (date) {
      formik.setFieldValue('endsAt', date.format('YYYY-MM-DDTHH:mm:ss[Z]'));
    } else {
      formik.setFieldValue('endsAt', '');
    }
  };

  const resetRangeStart = (date: Dayjs) => {
    setStartDateValue(date);
    setEndDateValue(null);
    setValidationError(null);
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    if (!newValue) return;

    const selectionAction = getDateRangeSelectionAction(startDate, endDate, newValue);

    if (selectionAction === 'reset-start') {
      resetRangeStart(newValue);
      return;
    }

    validateDates(startDate, newValue);
  };

  const validateDates = (start: Dayjs | null, end: Dayjs | null) => {
    if (start && end && end.isBefore(start.add(1, 'day'))) {
      setEndDateValue(null);
      setValidationError(t('companySurveys.modals.endDateValidation'));
    } else {
      setEndDateValue(end);
      setValidationError(null);
    }
  };

  const calendarStyles: SxProps = {
    backgroundColor: isReadOnly ? 'white' : orangeColors[5],
    width: '100%',
    mb: {xs: 2, sm: 0},
    borderRadius: '8px',
    border: isReadOnly ? `1px solid ${orangeColors[3]}` : 'none',

    '& .MuiDayCalendar-root': {
      width: '100%',
    },
    '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': {
      justifyContent: 'space-around',
    },
    '& .MuiPickersSlideTransition-root': {
      minHeight: '260px',
    },
    '& .MuiPickersDay-root': {
      fontSize: {xs: '16px', sm: '20px'},
      lineHeight: {xs: '24px', sm: '30px'},
      fontWeight: {xs: 300, sm: 400},
      textAlign: 'center',
      '&:hover': {
        backgroundColor: isReadOnly ? 'transparent' : orangeColors[3],
        borderRadius: '4px !important',
        transition: 'background-color 0.2s, border-radius 0.2s',
        padding: '4px',
      },
      '&.Mui-disabled': {
        color: greenColors[700],
        opacity: isReadOnly ? 0.5 : 1,
      },
    },
    '& .MuiTypography-root': {
      fontSize: {xs: '16px', sm: '20px'},
      lineHeight: {xs: '24px', sm: '30px'},
      fontWeight: {xs: 300, sm: 400},
      textAlign: 'center',
      '&:hover': {
        backgroundColor: isReadOnly ? 'transparent' : orangeColors[3],
        borderRadius: '4px !important',
        transition: 'background-color 0.2s, border-radius 0.2s',
        padding: '4px',
      },
    },
  };

  const arrowsStyles: SxProps = {
    backgroundColor: orangeColors[4],
    '&.MuiIconButton-root': {
      color: greenColors[700],
    },
  };

  const dayOfWeekFormatter = (_day: string, date: Dayjs) => {
    if (date && date?.format()) {
      return date.format('dd');
    } else {
      return '';
    }
  };

  const formatDate = (date: Dayjs | null) => {
    if (!date) return '-';
    return date.format('DD.MM.YYYY');
  };

  const isStartDate = (day: Dayjs) => Boolean(startDate?.isSame(day, 'day'));
  const isEndDate = (day: Dayjs) => Boolean(endDate?.isSame(day, 'day'));
  const isInSelectedRange = (day: Dayjs) =>
    Boolean(startDate && endDate && day.isAfter(startDate, 'day') && day.isBefore(endDate, 'day'));
  const isRangeBoundary = (day: Dayjs) => isStartDate(day) || isEndDate(day);

  const selectedValue = endDate || startDate;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mt: 3,
        backgroundColor: isReadOnly ? orangeColors[7] : 'transparent',
        border: isReadOnly ? `1px solid ${orangeColors[3]}` : 'none',
        borderRadius: isReadOnly ? '12px' : 0,
        padding: isReadOnly ? 2 : 0,
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLanguage}>
        <Box width='100%'>
          <Typography
            variant='h4'
            sx={{
              color: greenColors[700],
              mb: 1,
            }}
          >
            {t('companySurveys.modals.dateRangeTitle')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant='body2' color={greenColors[700]}>
              {t('companySurveys.modals.startDate')}: {formatDate(startDate)}
            </Typography>
            <Typography variant='body2' color={greenColors[700]}>
              {t('companySurveys.modals.endDate')}: {formatDate(endDate)}
            </Typography>
          </Box>
          <DateCalendar
            value={selectedValue}
            sx={calendarStyles}
            data-testid='calendarDateRange'
            disabled={isReadOnly}
            onChange={handleDateChange}
            disablePast
            disableHighlightToday={!isReadOnly}
            dayOfWeekFormatter={dayOfWeekFormatter}
            slots={{
              day: (props) => (
                <PickersDay
                  {...props}
                  disableTouchRipple
                  sx={{
                    borderRadius: '4px',
                    backgroundColor: isRangeBoundary(props.day)
                      ? greenColors[100]
                      : isInSelectedRange(props.day)
                        ? orangeColors[4]
                        : undefined,
                    color: isRangeBoundary(props.day) ? 'white' : undefined,
                    '&:hover': {
                      backgroundColor: isRangeBoundary(props.day) ? greenColors[500] : orangeColors[3],
                      borderRadius: '4px',
                    },
                    '&.MuiPickersDay-root.Mui-selected': {
                      backgroundColor: greenColors[700],
                      borderRadius: '4px',
                      color: 'white',
                      opacity: 1,
                      '&:hover': {
                        backgroundColor: greenColors[500],
                        borderRadius: '4px',
                        color: 'white',
                      },
                    },
                    '&.Mui-disabled.MuiPickersDay-root.Mui-selected': {
                      backgroundColor: greenColors[700],
                      color: 'white',
                      opacity: 1,
                    },
                    '&.MuiPickersDay-today': {
                      border: `1px solid ${greenColors[700]}`,
                      opacity: 1,
                    },
                  }}
                />
              ),
              calendarHeader: (props: PickersCalendarHeaderProps<Dayjs>) => (
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}} my={2} mx={2}>
                  <IconButton
                    onClick={() => props.onMonthChange(props.currentMonth.subtract(1, 'month'), 'left')}
                    sx={arrowsStyles}
                    disabled={isReadOnly}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography
                    variant='h4'
                    sx={{
                      '&.MuiTypography-root': {
                        fontSize: {xs: '16px', sm: '20px'},
                        lineHeight: {xs: '24px', sm: '30px'},
                        fontWeight: 700,
                        textAlign: 'center',
                        color: greenColors[700],
                      },
                    }}
                  >
                    {props.currentMonth.format('MMMM YYYY')}
                  </Typography>
                  <IconButton
                    onClick={() => props.onMonthChange(props.currentMonth.add(1, 'month'), 'right')}
                    sx={arrowsStyles}
                    disabled={isReadOnly}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              ),
            }}
          />
          {formik.errors && formik.errors.startsAt ? (
            <Typography variant='body2' color='error'>
              <ErrorMessage name={'startsAt'} component='span' />
            </Typography>
          ) : null}
          {validationError && (
            <Typography color='error' variant='body2'>
              {validationError}
            </Typography>
          )}
          {formik.errors && formik.errors.endsAt ? (
            <Typography variant='body2' color='error'>
              <ErrorMessage name={'endsAt'} component='span' />
            </Typography>
          ) : null}
        </Box>
      </LocalizationProvider>
    </Box>
  );
};
